import { Button, Input } from '@app/ui';
import { FadeIn, PageTransition } from '@app/ui/motion';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

export const Route = createFileRoute('/admin/organizations')({
  validateSearch: z.object({
    search: z.string().optional(),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.admin.organizations.queryOptions({
        input: { search: deps.search, page: deps.page },
      }),
    );
  },
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Organizations,
});

function Organizations() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const currentSearch = search.search || '';
  const [searchInput, setSearchInput] = useState(currentSearch);

  // Sync local input with URL changes (e.g. browser back/forward)
  useEffect(() => {
    setSearchInput(currentSearch);
  }, [currentSearch]);

  const { data } = useSuspenseQuery(
    api.admin.organizations.queryOptions({
      input: { search: search.search, page: search.page },
    }),
  );

  const { organizations, total, page, totalPages } = data;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== currentSearch) {
        navigate({ search: (prev) => ({ ...prev, search: searchInput || undefined, page: 1 }) });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, currentSearch, navigate]);

  return (
    <PageTransition>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Organizations ({total})</h1>
        <Input
          type="search"
          placeholder="Search organizations..."
          className="w-64"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>
      <p className="mt-2 text-muted-foreground">
        Manage organizations, members, and subscription plans.
      </p>

      <FadeIn>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Members</th>
                <th className="py-2 pr-4">Deals</th>
                <th className="py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {organizations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-muted-foreground">
                    No organizations found.
                  </td>
                </tr>
              ) : (
                organizations.map((org) => (
                  <tr key={org.id} className="border-b hover:bg-muted/50">
                    <td className="py-2 pr-4">
                      <div className="flex items-center gap-2">
                        {org.logo ? (
                          <img src={org.logo} alt="" className="w-6 h-6 rounded-full" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs">
                            {org.name.charAt(0)}
                          </div>
                        )}
                        {org.name}
                      </div>
                    </td>
                    <td className="py-2 pr-4 capitalize">{org.type}</td>
                    <td className="py-2 pr-4">{org.memberCount}</td>
                    <td className="py-2 pr-4">{org.dealCount}</td>
                    <td className="py-2 text-muted-foreground">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </FadeIn>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => navigate({ search: { ...search, page: page - 1 } })}
          >
            Previous
          </Button>
          <span className="flex items-center text-sm text-muted-foreground px-3">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => navigate({ search: { ...search, page: page + 1 } })}
          >
            Next
          </Button>
        </div>
      )}
    </PageTransition>
  );
}
